import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Send } from 'lucide-react';
import { BarreSites } from '../components/ui/BarreSites';
import { Modale } from '../components/ui/Modale';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  EnteteEcran,
  MessageErreur,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { useToast } from '../components/ui/useToast';
import { jourCivilReunion } from '../lib/dates-civiles';
import { DossierFactureFormateur } from '../components/factures-formateurs/DossierFactureFormateur';
import { ModalePaiement, resumeMontant } from '../components/factures-formateurs/GestesFactureFormateur';
import { usePerimetre } from '../site/usePerimetre';
import {
  useCircuitFactures,
  useDetailLot,
  usePayerLot,
  useTransmettreLot,
  type LigneFactureCircuit,
  type PropositionLot,
  type VueCircuit,
} from '../lib/useCircuitFactures';
import { dateReunion, euros, heures, jourCourt, messageRefus } from '../lib/useFormateurs';

/** « 10:32 », heure de La Réunion : deux lots d'un même jour se distinguent. */
function heureReunion(instant: string): string {
  return new Date(instant).toLocaleTimeString('fr-FR', {
    timeZone: 'Indian/Reunion',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function pluriel(nombre: number, singulier: string, plurielTexte = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielTexte : singulier}`;
}

const CLASSE_ACTION =
  'inline-flex min-h-[36px] shrink-0 items-center rounded-lg px-2 text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]';

/**
 * Circuit de facturation et transmission à la Direction (TF-401), sur la
 * maquette `fac-circuit` : de la fiche séquence au paiement par la Direction.
 *
 * Le pôle facturation y valide les factures vérifiées par l'assistant(e) et
 * transmet les lots — un type de formation × une antenne, CFA une fois la
 * semaine, OF chaque jour ; la Direction y ouvre les lots, note les paiements,
 * refuse ou annule. Tout vient de `GET /api/factures-formateurs` : compteurs,
 * propositions de lot, rythme et refus de chaque geste.
 */
export function FacCircuitPage(): JSX.Element {
  const { site } = usePerimetre();
  const { data, isLoading, error } = useCircuitFactures(site);
  const [parametres, setParametres] = useSearchParams();
  const [factureOuverte, setFactureOuverte] = useState<string | null>(null);
  const [lotOuvert, setLotOuvert] = useState<string | null>(null);
  const [aTransmettre, setATransmettre] = useState<PropositionLot | null>(null);

  // Un lien de notification ouvre la facture ou le lot ; l'adresse se vide une fois lue.
  useEffect(() => {
    const facture = parametres.get('facture');
    const lot = parametres.get('lot');
    if (!facture && !lot) return;
    if (facture) setFactureOuverte(facture);
    if (lot) setLotOuvert(lot);
    setParametres(
      (avant) => {
        const suivants = new URLSearchParams(avant);
        suivants.delete('facture');
        suivants.delete('lot');
        return suivants;
      },
      { replace: true },
    );
  }, [parametres, setParametres]);

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Circuit de facturation"
        sousTitre="Pôle facturation · de la fiche séquence au paiement par la Direction"
        actions={
          <>
            <Link
              to="/fac-formateurs"
              className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
            >
              Factures reçues
            </Link>
            <BarreSites />
          </>
        }
      />

      {isLoading && <Chargement libelle="Chargement du circuit…" />}
      {error && <MessageErreur message={messageRefus(error, 'Le circuit n’a pas pu être chargé.')} />}

      {data && (
        <>
          <Parcours etapes={data.etapes} />

          <Carte
            titre="Factures vérifiées à valider"
            actions={<span className="text-xs text-codialis-gray">{pluriel(data.aValider.length, 'facture')}</span>}
          >
            {data.aValider.length === 0 ? (
              <p className="px-4 py-5 text-sm text-codialis-gray sm:px-6">
                Aucune facture vérifiée par l’assistant(e) n’attend votre contrôle.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {data.aValider.map((facture) => (
                  <LigneAValider key={facture.id} facture={facture} onControler={() => setFactureOuverte(facture.id)} />
                ))}
              </ul>
            )}
          </Carte>

          <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
            <DossiersPrets vue={data} onTransmettre={setATransmettre} onOuvrirLot={setLotOuvert} />

            <div className="min-w-0 space-y-6">
              <SuiviPaiements suivi={data.suivi} />
              <Carte titre="Fréquence de traitement" sansBordure>
                <div className="space-y-2 p-4 sm:p-6">
                  {data.rythmes.map((rythme) => (
                    <div
                      key={rythme.type}
                      className="flex items-center justify-between gap-3 rounded-xl bg-gray-50 px-3 py-2.5"
                    >
                      <span className="min-w-0 text-sm text-codialis-navy">{rythme.detail}</span>
                      <Pastille texte={rythme.libelle} ton={rythme.type === 'CFA' ? 'info' : 'mint'} />
                    </div>
                  ))}
                  <p className="pt-1 text-[11px] text-codialis-gray">
                    Le rythme se rappelle dans le tableau de bord du pôle ; il ne bloque jamais une transmission.
                  </p>
                </div>
              </Carte>
            </div>
          </div>
        </>
      )}

      {aTransmettre && <ModaleTransmission proposition={aTransmettre} onFermer={() => setATransmettre(null)} />}
      {lotOuvert && <ModaleLot lotId={lotOuvert} onControler={setFactureOuverte} onFermer={() => setLotOuvert(null)} />}
      {factureOuverte && (
        <DossierFactureFormateur factureId={factureOuverte} onFermer={() => setFactureOuverte(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Parcours : ce qui attend à chaque étape
// ---------------------------------------------------------------------------

function Parcours({ etapes }: { etapes: VueCircuit['etapes'] }): JSX.Element {
  const lignes = [
    {
      titre: 'Fiche séquence',
      detail: 'Formateur saisit heures + émargement',
      attente: etapes.fichesACompleter,
      texte: `${pluriel(etapes.fichesACompleter, 'fiche')} à compléter`,
    },
    {
      titre: 'Validation pédagogique',
      detail: 'Responsable valide sous 8 jours',
      attente: etapes.fichesAValider,
      texte: `${pluriel(etapes.fichesAValider, 'fiche')} à valider`,
    },
    {
      titre: 'Dépôt facture',
      detail: 'SIRET, RIB, montant',
      attente: etapes.fichesAFacturer + etapes.facturesACorriger,
      texte: [
        `${pluriel(etapes.fichesAFacturer, 'fiche validée', 'fiches validées')} à facturer`,
        etapes.facturesACorriger > 0 ? `${pluriel(etapes.facturesACorriger, 'facture')} à corriger` : null,
      ]
        .filter(Boolean)
        .join(' · '),
    },
    {
      titre: 'Vérification',
      detail: 'Assistant(e) pédagogique / administratif, puis pôle facturation',
      attente: etapes.facturesAVerifier + etapes.facturesAValider + etapes.facturesATransmettre,
      texte: [
        `${etapes.facturesAVerifier} à vérifier`,
        `${etapes.facturesAValider} à valider`,
        `${etapes.facturesATransmettre} à transmettre`,
      ].join(' · '),
    },
    {
      titre: 'Direction',
      detail: 'Validation et paiement',
      attente: etapes.facturesChezDirection,
      texte: `${pluriel(etapes.facturesChezDirection, 'facture')} à payer`,
    },
  ];

  return (
    <section aria-labelledby="titre-parcours" className="rounded-2xl bg-white p-4 shadow-card sm:p-6 lg:p-8">
      <h2 id="titre-parcours" className="sr-only">
        Étapes du circuit
      </h2>
      <ol className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {lignes.map((ligne, rang) => (
          <li key={ligne.titre} className="min-w-0">
            <div
              className={`mb-3 flex h-10 w-10 items-center justify-center rounded-xl text-sm font-bold ${
                ligne.attente > 0 ? 'bg-codialis-navy text-white' : 'bg-gray-100 text-codialis-gray'
              }`}
              aria-hidden="true"
            >
              {rang + 1}
            </div>
            <div className="font-display text-sm font-bold text-codialis-navy">{ligne.titre}</div>
            <p className="mt-0.5 text-xs text-codialis-gray">{ligne.detail}</p>
            <p
              className={`mt-1 text-xs font-semibold ${ligne.attente > 0 ? 'text-codialis-navy' : 'text-codialis-gray'}`}
            >
              {ligne.texte}
            </p>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Factures à valider
// ---------------------------------------------------------------------------

function LigneAValider({
  facture,
  onControler,
}: {
  facture: LigneFactureCircuit;
  onControler: () => void;
}): JSX.Element {
  const ecart =
    facture.montantAttendu !== null ? Math.round((facture.montant - facture.montantAttendu) * 100) / 100 : null;
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5 sm:px-6">
      <div className="w-full min-w-0 sm:w-auto sm:flex-1">
        <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-codialis-navy">
          {facture.formateur.nom}
          <PastilleSite site={facture.siteCode} />
          {facture.typeLibelle && (
            <Pastille texte={facture.typeLibelle} ton={facture.type === 'CFA' ? 'info' : 'mint'} />
          )}
        </div>
        <div className="text-[11px] text-codialis-gray">
          Facture n° {facture.numero} · du {jourCourt(facture.debut)} au {jourCourt(facture.fin)} ·{' '}
          {heures(facture.heuresValidees)} · vérifiée le {dateReunion(facture.verifieeLe)}
          {facture.verifieePar ? ` par ${facture.verifieePar}` : ''}
        </div>
        {facture.refusValidation && <div className="mt-1 text-[11px] text-amber-700">{facture.refusValidation}</div>}
      </div>
      <div className="sm:text-right">
        <div className="font-display text-sm font-bold text-codialis-navy">{euros(facture.montant)}</div>
        {ecart !== null && ecart !== 0 && (
          <div className="text-[11px] text-amber-700">
            écart {ecart > 0 ? '+' : ''}
            {euros(ecart)}
          </div>
        )}
      </div>
      <button type="button" onClick={onControler} className={CLASSE_ACTION}>
        Contrôler{' '}
        <span className="sr-only">
          la facture n° {facture.numero} de {facture.formateur.nom}
        </span>
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Dossiers prêts pour la Direction : lots à transmettre, puis lots transmis
// ---------------------------------------------------------------------------

function DossiersPrets({
  vue,
  onTransmettre,
  onOuvrirLot,
}: {
  vue: VueCircuit;
  onTransmettre: (proposition: PropositionLot) => void;
  onOuvrirLot: (lotId: string) => void;
}): JSX.Element {
  const vide = vue.lotsATransmettre.length === 0 && vue.lots.length === 0;
  return (
    <Carte titre="Dossiers prêts pour la Direction">
      {vide && (
        <p className="px-4 py-5 text-sm text-codialis-gray sm:px-6">
          Aucune facture validée à transmettre, aucun lot chez la Direction.
        </p>
      )}
      {vue.lotsATransmettre.map((proposition) => (
        <div key={proposition.cle} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-4 sm:px-6">
          <div className="w-full min-w-0 sm:w-auto sm:flex-1">
            <div className="text-sm font-semibold text-codialis-navy">
              Lot {proposition.typeLibelle} · {proposition.siteCode}
            </div>
            <div className="text-[11px] text-codialis-gray">
              {resumeMontant(proposition.factures.length, proposition.montant)} · {proposition.rythme}
            </div>
            <div className="text-[11px] text-codialis-gray">{proposition.precision}</div>
          </div>
          <Pastille
            texte={proposition.du ? 'Prêt à transmettre' : 'Validé, lot suivant'}
            ton={proposition.du ? 'warning' : 'neutral'}
          />
          {proposition.refusTransmission === null && (
            <button type="button" onClick={() => onTransmettre(proposition)} className={CLASSE_ACTION}>
              Transmettre{' '}
              <span className="sr-only">
                le lot {proposition.typeLibelle} de {proposition.siteCode}
              </span>
            </button>
          )}
        </div>
      ))}
      {vue.lots.map((lot) => (
        <div key={lot.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-4 sm:px-6">
          <div className="w-full min-w-0 sm:w-auto sm:flex-1">
            <div className="text-sm font-semibold text-codialis-navy">{lot.libelle}</div>
            <div className="text-[11px] text-codialis-gray">
              {resumeMontant(lot.nombreFactures, lot.montant)} · transmis à {heureReunion(lot.transmisLe)}
              {lot.transmisPar ? ` par ${lot.transmisPar}` : ''}
              {lot.refusees > 0 ? ` · ${pluriel(lot.refusees, 'refusée')}` : ''}
            </div>
          </div>
          <Pastille texte={lot.etatLibelle} ton={lot.etatTon} />
          <button type="button" onClick={() => onOuvrirLot(lot.id)} className={CLASSE_ACTION}>
            Ouvrir <span className="sr-only">le {lot.libelle}</span>
          </button>
        </div>
      ))}
    </Carte>
  );
}

function SuiviPaiements({ suivi }: { suivi: VueCircuit['suivi'] }): JSX.Element {
  const lignes = [
    { libelle: 'Payées ce mois', compte: suivi.payeesCeMois },
    { libelle: 'En attente Direction', compte: suivi.enAttenteDirection },
    { libelle: 'À vérifier', compte: suivi.aVerifier },
    { libelle: 'Renvoyées au formateur', compte: suivi.renvoyees },
  ];
  return (
    <Carte titre="Suivi des paiements" sansBordure>
      <ul className="space-y-3 p-4 sm:p-6">
        {lignes.map((ligne) => (
          <li key={ligne.libelle} className="flex items-center gap-3 rounded-xl bg-gray-50 px-3 py-2.5">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-codialis-navy">{ligne.libelle}</div>
              <div className="text-[11px] text-codialis-gray">{pluriel(ligne.compte.nombre, 'facture')}</div>
            </div>
            <span className="font-display text-sm font-bold text-codialis-navy">
              {ligne.compte.nombre > 0 ? euros(ligne.compte.montant) : '-'}
            </span>
          </li>
        ))}
      </ul>
    </Carte>
  );
}

// ---------------------------------------------------------------------------
// Transmission d'un lot
// ---------------------------------------------------------------------------

function ModaleTransmission({
  proposition,
  onFermer,
}: {
  proposition: PropositionLot;
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const transmettre = useTransmettreLot();
  return (
    <Modale
      titre={`Transmettre le lot ${proposition.typeLibelle} · ${proposition.siteCode}`}
      sousTitre={`${resumeMontant(proposition.factures.length, proposition.montant)} · la Direction est prévenue`}
      ouverte
      onFermer={onFermer}
    >
      <div className="space-y-4 p-4 sm:p-6">
        <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100">
          {proposition.factures.map((facture) => (
            <li key={facture.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-sm">
              <span className="min-w-0 text-codialis-navy">
                {facture.formateur.nom} · n° {facture.numero}
              </span>
              <span className="font-semibold text-codialis-navy">{euros(facture.montant)}</span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-codialis-gray">
          {proposition.precision} Chaque formateur apprend que sa facture part à la Direction pour paiement.
        </p>
        {transmettre.error && (
          <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">
            {messageRefus(transmettre.error, 'Le lot n’a pas pu être transmis.')}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal
            disabled={transmettre.isPending}
            onClick={() =>
              transmettre.mutate(
                proposition.factures.map((facture) => facture.id),
                {
                  onSuccess: ({ lot }) => {
                    toast(`${lot.libelle} transmis à la Direction`);
                    onFermer();
                  },
                },
              )
            }
          >
            <Send className="mr-1.5 h-4 w-4" aria-hidden="true" />
            {transmettre.isPending ? 'Transmission…' : 'Transmettre à la Direction'}
          </BoutonPrincipal>
        </div>
      </div>
    </Modale>
  );
}

// ---------------------------------------------------------------------------
// Lot transmis : factures, coordonnées de paiement, gestes de la Direction
// ---------------------------------------------------------------------------

function ModaleLot({
  lotId,
  onControler,
  onFermer,
}: {
  lotId: string;
  onControler: (factureId: string) => void;
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const { data, isLoading, error } = useDetailLot(lotId);
  const payerLot = usePayerLot();
  const [paiement, setPaiement] = useState(false);
  const lot = data?.lot;
  const aPayer = lot?.factures.filter((facture) => facture.statut === 'TRANSMISE_DIRECTION') ?? [];
  const montantAPayer = aPayer.reduce((total, facture) => total + Math.round(facture.montant * 100), 0) / 100;

  return (
    <Modale
      titre={lot?.libelle ?? 'Lot de factures formateurs'}
      sousTitre={
        lot
          ? `${resumeMontant(lot.nombreFactures, lot.montant)} · transmis le ${dateReunion(lot.transmisLe)} à ${heureReunion(lot.transmisLe)}${
              lot.transmisPar ? ` par ${lot.transmisPar}` : ''
            }`
          : undefined
      }
      ouverte
      onFermer={onFermer}
      largeur="max-w-3xl"
    >
      <div className="space-y-4 p-4 sm:p-6">
        {isLoading && <Chargement libelle="Chargement du lot…" />}
        {error && <MessageErreur message={messageRefus(error, 'Le lot n’a pas pu être chargé.')} />}
        {lot && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Pastille texte={lot.etatLibelle} ton={lot.etatTon} />
              <PastilleSite site={lot.siteCode} />
              {lot.montantPaye > 0 && (
                <span className="text-xs text-codialis-gray">{euros(lot.montantPaye)} payés</span>
              )}
            </div>

            {lot.factures.length > 0 && (
              <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100">
                {lot.factures.map((facture) => (
                  <li key={facture.id} className="space-y-1.5 px-3 py-3 sm:px-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="min-w-0 text-sm font-semibold text-codialis-navy">
                        {facture.formateur.nom} · n° {facture.numero}
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="font-display text-sm font-bold text-codialis-navy">
                          {euros(facture.montant)}
                        </span>
                        <Pastille texte={facture.etatLibelle} ton={facture.etatTon} />
                      </span>
                    </div>
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
                      <div className="min-w-0">
                        <dt className="text-[10px] font-bold uppercase tracking-wide text-codialis-gray">IBAN</dt>
                        <dd className="break-all font-mono text-codialis-navy">{facture.iban ?? 'Illisible'}</dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-[10px] font-bold uppercase tracking-wide text-codialis-gray">
                          BIC · titulaire
                        </dt>
                        <dd className="break-words text-codialis-navy">
                          {[facture.bic, facture.titulaireCompte].filter(Boolean).join(' · ') || '—'}
                        </dd>
                      </div>
                    </dl>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-[11px] text-codialis-gray">
                        {facture.payeeLe
                          ? `Payée le ${jourCourt(facture.payeeLe)}${
                              facture.referencePaiement ? ` · réf. ${facture.referencePaiement}` : ''
                            }`
                          : `SIRET ${facture.siret}`}
                      </span>
                      <button type="button" onClick={() => onControler(facture.id)} className={CLASSE_ACTION}>
                        Contrôler{' '}
                        <span className="sr-only">
                          la facture n° {facture.numero} de {facture.formateur.nom}
                        </span>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {lot.facturesRefusees.length > 0 && (
              <section aria-labelledby={`refusees-${lot.id}`}>
                <h3
                  id={`refusees-${lot.id}`}
                  className="mb-2 text-[11px] font-bold uppercase tracking-wider text-codialis-gray"
                >
                  Refusées par la Direction
                </h3>
                <ul className="space-y-1.5 text-xs text-codialis-gray">
                  {lot.facturesRefusees.map((refus) => (
                    <li key={`${refus.factureId}-${refus.le}`}>
                      <span className="font-semibold text-codialis-navy">
                        {refus.formateur} · n° {refus.numero}
                      </span>{' '}
                      · le {dateReunion(refus.le)}
                      {refus.auteur ? ` par ${refus.auteur}` : ''} · {refus.motif}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {lot.refusPaiementLot === null && (
              <div className="flex flex-col gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-codialis-gray">
                  Reste à payer : {resumeMontant(aPayer.length, montantAPayer)}
                </p>
                <BoutonPrincipal onClick={() => setPaiement(true)}>Marquer le lot payé</BoutonPrincipal>
              </div>
            )}
          </>
        )}
      </div>

      {lot && paiement && (
        <ModalePaiement
          titre={`Paiement du ${lot.libelle}`}
          resume={resumeMontant(aPayer.length, montantAPayer)}
          transmisLe={jourCivilReunion(lot.transmisLe)}
          enCours={payerLot.isPending}
          erreur={payerLot.error}
          onConfirmer={(saisie) =>
            payerLot.mutate(
              { lotId: lot.id, factureIds: aPayer.map((facture) => facture.id), saisie },
              {
                onSuccess: () => {
                  toast('Paiement du lot noté · les formateurs sont prévenus');
                  setPaiement(false);
                },
              },
            )
          }
          onFermer={() => setPaiement(false)}
        />
      )}
    </Modale>
  );
}
